import {
	InfiniteQueryObserverOptions,
	QueryObserverOptions,
	UseQueryResult,
	useQueryClient,
	QueryKey as ReactQueryKey,
} from '@tanstack/react-query';
import { isUndefined } from '../../../predicates/type';
import { CustomObject } from '../../../types';
import { InfiniteQueryConfig, QueryConfig } from '../types';
import { useEffect, useLayoutEffect, useRef } from 'react';

declare global {
	interface Window {
		_DATA_QUERY_CACHE: CustomObject;
	}
}

const _DATA_QUERY_CACHE = {};
window._DATA_QUERY_CACHE = _DATA_QUERY_CACHE;

function parseEnabledProp(config: QueryConfig<unknown, unknown>) {
	if (!config.hasOwnProperty('enabled')) {
		return true;
	}

	return !!config.enabled;
}

function stringifyKey(key: unknown): string {
	return JSON.stringify(key);
}

const dataQueryInternals = {
	addInstance(key: string) {
		_DATA_QUERY_CACHE[key] = _DATA_QUERY_CACHE[key] || {
			count: 0,
		};

		_DATA_QUERY_CACHE[key].count++;
	},
	isEmpty(key: string) {
		return dataQueryInternals.getInstanceCount(key) === 0;
	},
	getInstanceCount(key: string) {
		return _DATA_QUERY_CACHE[key]?.count || 0;
	},
	removeInstance(key: string) {
		if (isUndefined(_DATA_QUERY_CACHE[key])) {
			return;
		}

		_DATA_QUERY_CACHE[key].count--;
	},
};

type CustomQueryConfig = QueryConfig | InfiniteQueryConfig;
type LibraryQueryConfig = QueryObserverOptions | InfiniteQueryObserverOptions;

function buildDataQueryConfig(customQueryConfig: CustomQueryConfig) {
	const newReactQueryConfig: Omit<LibraryQueryConfig, 'queryFn' | 'queryKey'> = {};
	const parsedEnabled = parseEnabledProp(customQueryConfig);

	if (typeof customQueryConfig.staleTime === 'number') {
		newReactQueryConfig.staleTime = customQueryConfig.staleTime;
	}

	if (!isUndefined(customQueryConfig.placeholderData)) {
		newReactQueryConfig.placeholderData = customQueryConfig.placeholderData;
	}

	if (!isUndefined(customQueryConfig.initialData)) {
		newReactQueryConfig.initialData = customQueryConfig.initialData;
	}

	newReactQueryConfig.enabled = parsedEnabled;

	if (typeof customQueryConfig.cacheTime === 'number') {
		newReactQueryConfig.gcTime = customQueryConfig.cacheTime;
	}

	return newReactQueryConfig;
}

function useRefetchReactQueryOnDemand({
	queryResult,
	queryKey,
	enabled,
}: {
	queryResult: UseQueryResult<unknown, Error>;
	queryKey: ReactQueryKey;
	enabled?: boolean;
}) {
	const queryResultRef = useRef(queryResult);
	const stringifiedKey = stringifyKey(queryKey);
	const queryClient = useQueryClient();

	useEffect(() => {
		if (dataQueryInternals.isEmpty(stringifiedKey)) {
			const queryData = queryClient.getQueryData(queryKey);
			const { isStale, isFetching, isLoading, isPending, isRefetching } = queryResultRef.current;
			const shouldRefetch =
				!isUndefined(queryData) &&
				enabled &&
				isStale &&
				!isFetching &&
				!isLoading &&
				!isPending &&
				!isRefetching;

			if (shouldRefetch) {
				console.log(`${stringifiedKey}: refetching`);

				queryClient.refetchQueries({
					queryKey: queryKey,
					exact: true,
				});
			}
		}

		dataQueryInternals.addInstance(stringifiedKey);

		return () => {
			dataQueryInternals.removeInstance(stringifiedKey);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryClient, stringifiedKey, enabled]);

	useLayoutEffect(() => {
		queryResultRef.current = queryResult;
	}, [queryResult]);
}

function useOnStateCallbacksReactQuery({
	queryResult,
	queryConfig,
}: {
	queryResult: UseQueryResult<unknown, Error>;
	queryConfig: QueryConfig;
}) {
	const {
		data,
		error,
		isError,
		isFetched,
		isFetching,
		isLoading,
		isSuccess,
		isPlaceholderData,
		isPending,
		isRefetching,
		isFetchedAfterMount,
	} = queryResult;
	const onSuccessRef = useRef(queryConfig.onSuccess);
	const onErrorRef = useRef(queryConfig.onError);

	useEffect(() => {
		if (
			isFetched &&
			isSuccess &&
			isFetchedAfterMount &&
			!isPlaceholderData &&
			!isLoading &&
			!isFetching &&
			!isPending &&
			!isRefetching &&
			!isError &&
			!isUndefined(data) &&
			typeof onSuccessRef.current === 'function'
		) {
			onSuccessRef.current(data);
		}
	}, [
		data,
		isFetched,
		isSuccess,
		isPlaceholderData,
		isLoading,
		isFetching,
		isPending,
		isError,
		isRefetching,
		isFetchedAfterMount,
	]);

	useEffect(() => {
		if (
			isFetchedAfterMount &&
			!isLoading &&
			!isFetching &&
			!isPending &&
			!isRefetching &&
			error &&
			typeof onErrorRef.current === 'function'
		) {
			console.log(error);
			onErrorRef.current(error);
		}
	}, [error, isFetchedAfterMount, isFetching, isLoading, isPending, isRefetching]);

	useLayoutEffect(() => {
		onSuccessRef.current = queryConfig.onSuccess;
	}, [queryConfig.onSuccess]);

	useLayoutEffect(() => {
		onErrorRef.current = queryConfig.onError;
	}, [queryConfig.onError]);
}

function normalizeReactQueryKey<T>(queryKey: T) {
	return Array.isArray(queryKey) ? queryKey : [queryKey];
}

export {
	parseEnabledProp,
	stringifyKey,
	dataQueryInternals,
	buildDataQueryConfig,
	useRefetchReactQueryOnDemand,
	useOnStateCallbacksReactQuery,
	normalizeReactQueryKey,
};
